// src/modules/leads/MergeLeadModal.js
// Search-and-merge: find the lead that was created from a second phone number
// and absorb its communications + numbers into the current (original) lead.
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FaSearch, FaPhoneAlt, FaExclamationTriangle, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import Modal from '../../components/shared/Modal';
import formatPhone from '../../components/shared/formatPhone';
import { searchLeads } from './leadService';
import { mergeLeadInto } from './leadPhoneService';

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid ${p => p.theme.colors.inputGray};
  border-radius: ${p => p.theme.borderRadius};
  padding: 8px 12px;
  margin-bottom: 12px;

  svg { color: ${p => p.theme.colors.textSecondary}; flex-shrink: 0; }

  input {
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
    color: ${p => p.theme.colors.textPrimary};
    font-size: 0.95rem;
    font-family: inherit;
  }
`;

const ResultsList = styled.div`
  max-height: 320px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ResultItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid transparent;
  border-radius: ${p => p.theme.borderRadius};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${p => p.theme.colors.primary}55;
    background: ${p => p.theme.colors.primary}11;
  }

  .name { font-weight: 600; color: ${p => p.theme.colors.textPrimary}; }
  .meta {
    font-size: 0.82rem;
    color: ${p => p.theme.colors.textSecondary};
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const Hint = styled.p`
  color: ${p => p.theme.colors.textSecondary};
  font-size: 0.85rem;
  text-align: center;
  margin: 16px 0;
`;

const ErrorText = styled.p`
  color: ${p => p.theme.colors.error};
  font-size: 0.85rem;
  margin: 8px 0;
`;

const ConfirmBox = styled.div`
  background: ${p => p.theme.colors.error}11;
  border: 1px solid ${p => p.theme.colors.error}44;
  border-radius: ${p => p.theme.borderRadius};
  padding: 16px;
  margin-bottom: 16px;

  .warn-head {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${p => p.theme.colors.error};
    font-weight: 600;
    margin-bottom: 8px;
  }

  p { margin: 0; color: ${p => p.theme.colors.textPrimary}; font-size: 0.9rem; line-height: 1.5; }
  strong { color: ${p => p.theme.colors.textPrimary}; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: ${p => p.theme.borderRadius};
  border: none;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.9rem;

  &:disabled { opacity: 0.6; cursor: not-allowed; }

  ${p => p.$variant === 'danger' && `
    background: ${p.theme.colors.error};
    color: #fff;
  `}
  ${p => (!p.$variant || p.$variant === 'ghost') && `
    background: transparent;
    color: ${p.theme.colors.textSecondary};
    border: 1px solid ${p.theme.colors.inputGray};
  `}
`;

const isPlaceholder = (lead) =>
  Boolean(lead?.isPlaceholder || lead?.is_placeholder || lead?.placeholder || lead?.type === 'placeholder');

const MergeLeadModal = ({ isOpen, currentLead, onClose, onMerged }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null); // candidate lead, triggers confirm step
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const debounceRef = useRef(null);

  // Reset everything whenever the modal is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setSearchError(null);
      setMergeError(null);
    }
  }, [isOpen]);

  // Debounced search.
  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        setSearchError(null);
        const response = await searchLeads(q);
        const data = response?.data;
        const list = Array.isArray(data) ? data : (data?.leads || data?.results || []);
        setResults(list.filter(l => String(l.id) !== String(currentLead?.id)));
      } catch (err) {
        console.error('Lead search failed:', err);
        setSearchError('Search failed. Please try again.');
      } finally {
        setSearching(false);
      }
    }, 350);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, isOpen, currentLead]);

  const handleConfirmMerge = async () => {
    if (!selected || !currentLead?.id) return;
    try {
      setMerging(true);
      setMergeError(null);
      const sourceType = isPlaceholder(selected) ? 'placeholder' : 'lead';
      const response = await mergeLeadInto(currentLead.id, selected.id, sourceType);
      onMerged(response?.data, selected);
      onClose();
    } catch (err) {
      console.error('Merge failed:', err);
      setMergeError(
        err?.response?.data?.message || err?.message || 'Merge failed. Please try again.'
      );
    } finally {
      setMerging(false);
    }
  };

  if (!isOpen) return null;

  const candidateMeta = (lead) =>
    [formatPhone(lead.phone || ''), lead.city, lead.origin?.origin_name]
      .filter(Boolean)
      .join(' · ');

  return (
    <Modal
      isOpen={isOpen}
      title={selected ? 'Confirm merge' : `Link a number to ${currentLead?.name || 'this lead'}`}
      onCancel={onClose}
      hideFooter
      disableBackdropClose={merging}
    >
      {!selected ? (
        <>
          <SearchRow>
            <FaSearch />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or phone number…"
            />
          </SearchRow>

          {searchError && <ErrorText>{searchError}</ErrorText>}

          {searching ? (
            <Hint><FaSpinner className="icon-spin" /> Searching…</Hint>
          ) : query.trim().length < 2 ? (
            <Hint>Type a name or phone number to find the duplicate lead to merge in.</Hint>
          ) : results.length === 0 ? (
            <Hint>No matching leads found.</Hint>
          ) : (
            <ResultsList>
              {results.map((lead) => (
                <ResultItem key={lead.id} type="button" onClick={() => setSelected(lead)}>
                  <span className="name">{lead.name || 'Unnamed lead'}</span>
                  <span className="meta"><FaPhoneAlt size={11} /> {candidateMeta(lead) || '—'}</span>
                </ResultItem>
              ))}
            </ResultsList>
          )}
        </>
      ) : (
        <>
          <ConfirmBox>
            <div className="warn-head">
              <FaExclamationTriangle /> This can't be undone
            </div>
            <p>
              All calls, messages and phone numbers from{' '}
              <strong>{selected.name || 'the selected lead'}</strong>
              {selected.phone ? ` (${formatPhone(selected.phone)})` : ''} will be moved into{' '}
              <strong>{currentLead?.name || 'this lead'}</strong>, and the duplicate lead will be
              deleted.
            </p>
          </ConfirmBox>

          {mergeError && <ErrorText>{mergeError}</ErrorText>}

          <Actions>
            <Btn type="button" onClick={() => { setSelected(null); setMergeError(null); }} disabled={merging}>
              <FaArrowLeft /> Back
            </Btn>
            <Btn type="button" $variant="danger" onClick={handleConfirmMerge} disabled={merging}>
              {merging ? <><FaSpinner className="icon-spin" /> Merging…</> : 'Merge & delete duplicate'}
            </Btn>
          </Actions>
        </>
      )}
    </Modal>
  );
};

export default MergeLeadModal;
